import { useState } from "react";
import { Link } from "react-router";
import { CheckCircle2, ChevronRight, Lightbulb, ShieldCheck, Sparkles, TriangleAlert } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { Badge, Button, Card, cx, Field, NumberStepper, SectionTitle, Spinner, TextField } from "@/components/ui";
import { KidChips, Points } from "@/components/bits";
import { api } from "@/lib/api";
import { useAction } from "@/lib/queries";
import { useSession } from "@/lib/session";
import type { EconomyCheckDTO, JobPriceDTO } from "@shared/types";

function SourceBadge(_props: { source: "built-in" }) {
  return <Badge tone="neutral">Built-in suggestions</Badge>;
}

function EconomyCheck() {
  const { family, kids } = useSession();
  const [result, setResult] = useState<EconomyCheckDTO | null>(null);
  const run = useAction(() => api.post<EconomyCheckDTO>("/advisor/economy-check"), { onSuccess: (r) => setResult(r) });
  return (
    <Card>
      <div className="flex items-start gap-3">
        <span className="flex size-11 shrink-0 items-center justify-center rounded-2xl bg-brand-soft text-2xl">🩺</span>
        <div className="flex-1">
          <div className="font-extrabold">Economy check-up</div>
          <p className="text-sm text-muted">Is earning potential fair for each kid's age? Are rewards reachable? Are caps and consequences balanced?</p>
        </div>
      </div>
      {run.isPending ? (
        <div className="mt-4 flex items-center gap-3 rounded-2xl bg-sunken p-4 text-sm font-bold text-muted">
          <Spinner className="size-5" /> Looking over your setup…
        </div>
      ) : result ? (
        <div className="mt-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <p className="font-bold">{result.summary}</p>
            <SourceBadge source={result.source} />
          </div>
          {kids.length > 0 && (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {kids.map((k) => (
                <div key={k.id} className="rounded-2xl bg-sunken px-3 py-2">
                  <div className="text-xs font-bold text-muted">{k.name} · max / week</div>
                  <Points value={result.weeklyEarningPotential[k.id] ?? 0} family={family} className="text-lg" />
                </div>
              ))}
            </div>
          )}
          {result.findings.map((f, i) => (
            <div
              key={i}
              className={cx(
                "flex gap-2 rounded-2xl p-3 text-sm",
                f.level === "good" ? "bg-brand-soft" : f.level === "warn" ? "bg-warn-soft" : "bg-sunken",
              )}
            >
              {f.level === "good" ? (
                <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-brand" />
              ) : f.level === "warn" ? (
                <TriangleAlert className="mt-0.5 size-4 shrink-0 text-warn" />
              ) : (
                <Lightbulb className="mt-0.5 size-4 shrink-0 text-violet" />
              )}
              <div>
                <div className="font-extrabold">{f.title}</div>
                <div className="text-muted">{f.detail}</div>
              </div>
            </div>
          ))}
          <Button size="sm" variant="secondary" onClick={() => run.mutate()}>
            Run again
          </Button>
        </div>
      ) : (
        <Button className="mt-4" onClick={() => run.mutate()}>
          <Sparkles className="size-4" /> Run check-up
        </Button>
      )}
    </Card>
  );
}

function PriceAJob() {
  const { family, kids } = useSession();
  const [title, setTitle] = useState("");
  const [minutes, setMinutes] = useState(30);
  const [kidIds, setKidIds] = useState<string[]>([]);
  const [result, setResult] = useState<JobPriceDTO | null>(null);
  const price = useAction(
    () => api.post<JobPriceDTO>("/advisor/price-job", { title, estimatedMinutes: minutes, kidId: kidIds.length === 1 ? kidIds[0] : undefined }),
    { onSuccess: (r) => setResult(r) },
  );
  const create = useAction(
    () => api.post("/jobs", { title, reward: result!.suggestedReward, kidIds, estimatedMinutes: minutes, repeat: "anytime" }),
    {
      success: "Job created — kids can see it now",
      onSuccess: () => {
        setTitle("");
        setResult(null);
      },
    },
  );
  return (
    <Card>
      <div className="flex items-start gap-3">
        <span className="flex size-11 shrink-0 items-center justify-center rounded-2xl bg-coin-soft text-2xl">🏷️</span>
        <div className="flex-1">
          <div className="font-extrabold">Price a job</div>
          <p className="text-sm text-muted">Get a fair reward for any job, based on time, effort and age.</p>
        </div>
      </div>
      <div className="mt-4 space-y-4">
        <TextField label="Job" placeholder="Clean out the car" value={title} maxLength={80} onChange={(e) => setTitle(e.target.value)} />
        <Field label="About how long?">
          <NumberStepper value={minutes} onChange={setMinutes} min={5} max={480} step={5} suffix="min" />
        </Field>
        {kids.length > 1 && (
          <Field label="For">
            <KidChips kids={kids} value={kidIds} onChange={setKidIds} />
          </Field>
        )}
        <Button onClick={() => price.mutate()} loading={price.isPending} disabled={!title.trim()}>
          <Sparkles className="size-4" /> Suggest a price
        </Button>
        {result && (
          <div className="rounded-2xl bg-sunken p-4">
            <div className="flex items-center justify-between gap-2">
              <Points value={result.suggestedReward} family={family} className="text-3xl text-coin-ink" showName />
              <SourceBadge source={result.source} />
            </div>
            <div className="mt-1 text-sm font-bold text-muted">
              Fair range {result.low}–{result.high}
            </div>
            <p className="mt-2 text-sm">{result.rationale}</p>
            <Button className="mt-3" size="sm" onClick={() => create.mutate()} loading={create.isPending}>
              Create this job
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

const TOOLS = [
  { to: "/parent/plan/store", icon: "🛍️", title: "Design your family store", text: "A menu of rewards tailored to each kid's interests, fairly priced." },
  { to: "/parent/plan/rewards", icon: "⏱️", title: "Screen-time caps", text: "Age-appropriate weekly and daily limits plus schedule rules." },
  { to: "/parent/plan/discipline", icon: "⚖️", title: "Consequence presets", text: "Proportionate, recoverable penalties sized to what kids earn." },
  { to: "/parent/approvals", icon: "💡", title: "Review kid pitches", text: "Get a fairness check and a coaching tip for every pitch." },
];

export function Advisor() {
  return (
    <div>
      <PageHeader title="Advisor" subtitle="Suggestions for a fair system" bell="/parent/notifications" />
      <Card className="mt-3 flex gap-3 bg-violet-soft ring-violet/30">
        <ShieldCheck className="mt-0.5 size-5 shrink-0 text-violet" />
        <div className="text-sm">
          <div className="font-extrabold">Built-in rules, no AI</div>
          <p className="mt-0.5 text-muted">
            Suggestions come from rules of thumb for kids' ages, prices and caps, worked out on our servers. Nothing about your family is sent to an
            AI service, and the advisor never looks at chores, check-offs, notes or balances.
          </p>
        </div>
      </Card>

      <SectionTitle>Check your economy</SectionTitle>
      <EconomyCheck />

      <SectionTitle>Quick pricing</SectionTitle>
      <PriceAJob />

      <SectionTitle>More advisor tools</SectionTitle>
      <div className="grid gap-2 lg:grid-cols-2">
        {TOOLS.map((t) => (
          <Link key={t.to} to={t.to}>
            <Card className="flex h-full items-center gap-3 p-3 transition hover:ring-violet/50">
              <span className="text-2xl">{t.icon}</span>
              <div className="min-w-0 flex-1">
                <div className="font-extrabold">{t.title}</div>
                <div className="text-sm text-muted">{t.text}</div>
              </div>
              <ChevronRight className="size-5 text-faint" />
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
